import React, { useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Check, ChevronDown, Radio } from "lucide-react";
import Magnetic from "@/studio/components/Magnetic";

/**
 * (05) PROJECT ARCHITECT — a mini technical consultation.
 * The recommended stack AND its reasoning change with the selected project type
 * and features. Every technology explains: what it powers, why for THIS project,
 * the client benefit, and the alternative we considered.
 * No confidence scores, no fake percentages, no fake metrics.
 */

const ease = [0.16, 1, 0.3, 1];

const PROJECT_TYPES = [
  { id: "website", label: "Website", base: 1 },
  { id: "ecommerce", label: "E-Commerce", base: 3 },
  { id: "saas", label: "SaaS", base: 4 },
  { id: "ai", label: "AI Assistant", base: 3 },
  { id: "internal", label: "Internal System", base: 3 },
  { id: "trading", label: "Trading Platform", base: 5 },
  { id: "mobile", label: "Mobile App", base: 4 },
  { id: "other", label: "Other", base: 2 },
];

const FEATURES = [
  { id: "admin", label: "Admin Panel" },
  { id: "auth", label: "Authentication" },
  { id: "realtime", label: "Realtime Updates" },
  { id: "ai", label: "AI Features" },
  { id: "payments", label: "Payments" },
  { id: "crm", label: "CRM" },
  { id: "blog", label: "Blog" },
  { id: "analytics", label: "Analytics" },
  { id: "seo", label: "SEO" },
  { id: "api", label: "API Integrations" },
  { id: "i18n", label: "Multi-language" },
  { id: "chat", label: "Chat" },
];

const TRAFFIC = [
  { id: "small", label: "Small", weight: 0 },
  { id: "medium", label: "Medium", weight: 1 },
  { id: "large", label: "Large", weight: 2 },
  { id: "enterprise", label: "Enterprise", weight: 3 },
];

/* ─── Per-type core layers (Frontend / Backend / Database) ─── */
const CORE = {
  website: {
    frontend: {
      slug: "frontend", layer: "Frontend", tech: "React / Next.js",
      powers: "Landing pages, blog and a light, CMS-driven marketing site.",
      why: "This site has to be found and convert, so we use Next.js for server rendering, clean SEO control and instant first paint.",
      benefit: "Better rankings, faster load, and content the team can edit without a developer.",
      alt: { name: "a website builder (Webflow / Wix)", reason: "Builders are great for a quick brochure, but they cap custom interactions, integrations and the performance tuning a serious site needs." },
    },
    backend: {
      slug: "backend", layer: "Backend", tech: "FastAPI",
      powers: "Form handling, CMS API and integrations (newsletter, CRM, analytics).",
      why: "Even a 'simple' site needs a reliable home for forms, content and third-party hooks — FastAPI keeps that fast and tidy.",
      benefit: "Contact and content just work, and new integrations are easy to add later.",
      alt: { name: "no backend at all", reason: "Pure static is fine until you need forms, dynamic content or integrations — then you're retrofitting one." },
    },
    database: {
      slug: "database", layer: "Database", tech: "MongoDB",
      powers: "Pages, posts, media and form submissions.",
      why: "Content models change often on a marketing site, so a flexible document store lets us iterate without migrations.",
      benefit: "You can restructure content freely as the brand evolves.",
      alt: { name: "a relational DB (PostgreSQL)", reason: "Rigid tables slow down the content changes that happen constantly on a marketing site." },
    },
  },
  ecommerce: {
    frontend: {
      slug: "frontend", layer: "Frontend", tech: "React / Next.js",
      powers: "Storefront, product catalogue, cart and a custom checkout UI.",
      why: "Shoppers abandon slow stores, so we use Next.js for fast product pages, SEO and a smooth, conversion-tuned checkout.",
      benefit: "More products found, faster pages, and a checkout built to convert.",
      alt: { name: "Shopify's themes alone", reason: "Shopify is great to start, but its theme layer limits custom UX, bundles and deep integrations once you scale." },
    },
    backend: {
      slug: "backend", layer: "Backend", tech: "FastAPI",
      powers: "Orders, inventory, pricing and payment / shipping integrations.",
      why: "Your catalogue and orders need custom logic, so we run a real backend instead of bending a closed platform.",
      benefit: "Full control over pricing, stock and fulfilment as the business grows.",
      alt: { name: "an all-in-one closed store", reason: "Hosted platforms trade flexibility for convenience — painful the moment you need custom or B2B flows." },
    },
    database: {
      slug: "database", layer: "Database", tech: "MongoDB",
      powers: "Products, variants, orders and customers.",
      why: "Product data is deeply nested and changes often, which maps naturally onto documents.",
      benefit: "New product types and attributes are trivial to add.",
      alt: { name: "PostgreSQL", reason: "Relational schemas can model commerce, but they fight you on flexible, nested catalogue data." },
    },
  },
  saas: {
    frontend: {
      slug: "frontend", layer: "Frontend", tech: "React",
      powers: "Dashboards, account area, role-based admin and settings.",
      why: "A SaaS is an app, not a page — React's component model keeps complex, stateful dashboards maintainable.",
      benefit: "A snappy product that grows feature-by-feature without rebuilds.",
      alt: { name: "a no-code builder (Bubble)", reason: "No-code is fast early, but hits walls on custom logic, performance and ownership exactly when you scale." },
    },
    backend: {
      slug: "backend", layer: "Backend", tech: "FastAPI",
      powers: "Authentication, roles, subscriptions, APIs and business logic.",
      why: "Subscriptions, permissions and APIs need a fast, typed backend — FastAPI gives async performance and clean contracts.",
      benefit: "Secure multi-user access and APIs ready for integrations and a future mobile app.",
      alt: { name: "a heavy monolith (Django) by default", reason: "It works, but FastAPI's async, schema-first design is leaner for API- and AI-heavy SaaS." },
    },
    database: {
      slug: "database", layer: "Database", tech: "MongoDB / PostgreSQL",
      powers: "Users, tenants, subscriptions and product data.",
      why: "Early SaaS iterates fast, so we start document-flexible and add PostgreSQL where strict relations (billing, ledgers) demand it.",
      benefit: "Move fast now, stay correct where money and consistency matter.",
      alt: { name: "PostgreSQL for absolutely everything", reason: "Rigid schemas slow early iteration; we use relational only where consistency truly requires it." },
    },
  },
  ai: {
    frontend: {
      slug: "frontend", layer: "Frontend", tech: "React",
      powers: "Chat / assistant UI, streaming responses and an admin view.",
      why: "Assistant UIs need live streaming and stateful conversations, which React handles cleanly.",
      benefit: "A responsive assistant that feels instant and is easy to extend.",
      alt: { name: "a basic embedded chat widget", reason: "Off-the-shelf widgets can't do custom workflows, tools or your own knowledge base." },
    },
    backend: {
      slug: "backend", layer: "Backend", tech: "FastAPI + Python",
      powers: "Prompt orchestration, queues, tool-calling and integrations.",
      why: "AI features need server-side control of prompts, queues and cost — and Python is where that ecosystem actually lives.",
      benefit: "Reliable AI features you can tune for quality and token spend.",
      alt: { name: "a Node.js backend", reason: "Node is fine for I/O, but the AI/ML ecosystem — embeddings, pipelines, SDKs — is richest in Python." },
    },
    database: {
      slug: "database", layer: "Database", tech: "MongoDB",
      powers: "Conversations, knowledge base and vector-ready storage.",
      why: "RAG content and chat logs are document-shaped and grow fast, which fits MongoDB well.",
      benefit: "A knowledge base and history that scale with usage.",
      alt: { name: "a plain SQL table", reason: "Relational rows are awkward for nested messages, metadata and embeddings." },
    },
  },
  internal: {
    frontend: {
      slug: "frontend", layer: "Frontend", tech: "React",
      powers: "Admin panels, internal dashboards and workflow screens.",
      why: "Internal tools live on dense, role-specific screens — React keeps these maintainable for your team.",
      benefit: "Staff get tools that fit their process, not 30 features they ignore.",
      alt: { name: "spreadsheets / Airtable", reason: "Sheets break down once you need real workflows, permissions and data volume." },
    },
    backend: {
      slug: "backend", layer: "Backend", tech: "FastAPI",
      powers: "Roles, permissions, workflows and automation.",
      why: "Internal systems are all about rules and automation, so we put solid role logic and integrations in FastAPI.",
      benefit: "Processes that run with fewer errors and far less manual work.",
      alt: { name: "a no-code internal-tool builder", reason: "Builders are quick for CRUD, but stall on custom workflows, automation and integrations." },
    },
    database: {
      slug: "database", layer: "Database", tech: "PostgreSQL / MongoDB",
      powers: "Records, audit trails, roles and relationships.",
      why: "Internal data is often relational and audited, so PostgreSQL leads, with MongoDB where data is more flexible.",
      benefit: "Trustworthy, consistent records your team can rely on.",
      alt: { name: "MongoDB for everything", reason: "Documents are flexible, but strict reporting and relations are cleaner in PostgreSQL." },
    },
  },
  trading: {
    frontend: {
      slug: "frontend", layer: "Frontend", tech: "React",
      powers: "Charts, order tickets, live tables and dashboards.",
      why: "Trading UIs update constantly and must stay responsive under load — React's rendering keeps fast feeds smooth.",
      benefit: "A fast, dense interface traders can trust in real time.",
      alt: { name: "a templated dashboard kit", reason: "Pre-built dashboards can't keep up with custom, high-frequency, real-time UI." },
    },
    backend: {
      slug: "backend", layer: "Backend", tech: "FastAPI",
      powers: "Order handling, event processing and market-data fan-out.",
      why: "Sub-second events need an async backend, so FastAPI processes and broadcasts without blocking.",
      benefit: "Orders and data move instantly and reliably.",
      alt: { name: "a synchronous / blocking backend", reason: "Blocking servers choke under real-time event load." },
    },
    database: {
      slug: "database", layer: "Database", tech: "MongoDB / PostgreSQL",
      powers: "Trades, positions, orders and time-series events.",
      why: "High-write event and time-series data suits MongoDB, with PostgreSQL for strict financial records.",
      benefit: "Fast writes plus correctness where money is involved.",
      alt: { name: "one relational DB under heavy write load", reason: "A single relational DB can bottleneck on high-frequency writes without careful scaling." },
    },
  },
  mobile: {
    frontend: {
      slug: "frontend", layer: "Frontend", tech: "React Native / PWA",
      powers: "Cross-platform app UI (iOS + Android) or an installable PWA.",
      why: "You want mobile reach without building two native apps, so React Native shares one codebase (a PWA covers lighter scope).",
      benefit: "One team ships to both platforms — faster and cheaper to maintain.",
      alt: { name: "separate native iOS + Android apps", reason: "Two native codebases double the cost and slow every release — only worth it for heavy device-specific needs." },
    },
    backend: {
      slug: "backend", layer: "Backend", tech: "FastAPI",
      powers: "Mobile APIs, auth, push notifications and sync.",
      why: "A mobile app is a thin client over an API, so we give it a fast, typed FastAPI backend.",
      benefit: "One backend powers the app today and the web tomorrow.",
      alt: { name: "a backend-as-a-service for everything", reason: "BaaS is quick to start, but limits custom logic and can get costly and lock you in." },
    },
    database: {
      slug: "database", layer: "Database", tech: "MongoDB",
      powers: "User data, content and sync state.",
      why: "Mobile data models evolve fast across app versions, which documents handle gracefully.",
      benefit: "Ship app updates without painful migrations.",
      alt: { name: "on-device storage only", reason: "Local-only storage can't sync across devices or back up to the cloud." },
    },
  },
  other: {
    frontend: {
      slug: "frontend", layer: "Frontend", tech: "React",
      powers: "The product interface and admin.",
      why: "React gives a flexible, component-based UI that fits almost any custom product.",
      benefit: "A maintainable interface that can grow in any direction.",
      alt: { name: "a website builder", reason: "Builders cap the custom logic and integrations a real product needs." },
    },
    backend: {
      slug: "backend", layer: "Backend", tech: "FastAPI",
      powers: "APIs, business logic and integrations.",
      why: "FastAPI gives high-performance async APIs with clean, typed contracts.",
      benefit: "A solid foundation ready for integrations and scale.",
      alt: { name: "a heavier framework by default", reason: "FastAPI stays lean and fast, especially for API- and AI-driven work." },
    },
    database: {
      slug: "database", layer: "Database", tech: "MongoDB",
      powers: "Application data.",
      why: "A flexible document store lets us iterate quickly while requirements firm up.",
      benefit: "Change the data model without slow migrations.",
      alt: { name: "a relational DB up front", reason: "Rigid schemas slow iteration before the domain is settled." },
    },
  },
};

/* ─── Specialized + shared layers ─── */
const SPECIAL = {
  realtime: {
    slug: "realtime", layer: "Realtime", tech: "WebSockets",
    powers: "Live prices, chat, presence and instant updates.",
    why: "This project shows data that changes by the second, so we push updates over a persistent socket instead of asking the server again and again.",
    benefit: "Users see changes the instant they happen, with low latency and no manual refreshing.",
    alt: { name: "polling the API on a timer", reason: "Polling is simple but wasteful and laggy — it hammers the server and still arrives late." },
  },
  ai: {
    slug: "ai", layer: "AI Layer", tech: "OpenAI + Python",
    powers: "Assistants, retrieval, content generation and prompt workflows.",
    why: "Your project needs real language understanding, so we run model calls and data pipelines in Python — the ecosystem AI tooling lives in.",
    benefit: "Dependable AI features you can tune for quality and token cost, not a black box.",
    alt: { name: "wiring an LLM straight into the frontend", reason: "Calling models from the browser leaks keys, can't cache or queue, and gives no control over cost or quality." },
  },
  payments: {
    slug: "payments", layer: "Payments", tech: "Stripe",
    powers: "Checkout, subscriptions, invoicing and payouts.",
    why: "Money flows through this product, so we use Stripe's hardened, PCI-compliant infrastructure instead of touching card data ourselves.",
    benefit: "Customers pay through a trusted, secure flow that supports cards, wallets and subscriptions worldwide.",
    alt: { name: "a manual / bank-transfer flow", reason: "Manual payments don't scale, lack automation and create reconciliation headaches as volume grows." },
  },
};

function edgeCard(trafficLabel) {
  return {
    slug: "edge", layer: "Edge & Security", tech: "Cloudflare",
    powers: "CDN caching, DDoS protection and DNS at the edge.",
    why: `You expect ${trafficLabel.toLowerCase()} traffic, so we put Cloudflare in front to absorb load and attacks before they reach the app.`,
    benefit: "The product stays fast and online under spikes and abuse, everywhere in the world.",
    alt: { name: "serving everything from origin", reason: "Without an edge layer, every request hits your server directly — slower globally and exposed to attack." },
  };
}

function motionCard(typeName) {
  return {
    slug: "motion", layer: "Motion", tech: "GSAP",
    powers: "Scroll choreography, transitions and interface micro-motion.",
    why: `${typeName} earns trust in the first seconds, so we use GSAP for precise, cinematic motion instead of fragile CSS hacks.`,
    benefit: "The product feels considered and premium before a single word is read.",
    alt: { name: "CSS-only animation", reason: "CSS handles hovers, but can't choreograph timelines, pinning and scroll-synced sequences cleanly." },
  };
}

function deploymentCard(typeName) {
  return {
    slug: "deployment", layer: "Deployment", tech: "Vercel",
    powers: "Build pipeline, global edge hosting and preview deploys.",
    why: `So your ${typeName.toLowerCase()} ships continuously, we deploy to Vercel's global edge with a preview link on every change.`,
    benefit: "Updates go live in seconds worldwide, with sign-off previews and simple rollbacks.",
    alt: { name: "a single self-managed VPS", reason: "A lone server means manual ops, no global edge and real downtime risk." },
  };
}

const MOTION_TYPES = ["website", "ecommerce", "saas", "ai", "mobile", "other"];

function Chip({ active, onClick, children, testid, multi }) {
  return (
    <button
      type="button"
      data-cursor="big"
      data-testid={testid}
      onClick={onClick}
      className={`rounded-full border px-4 py-2.5 text-[14px] font-medium tracking-tight transition-colors duration-300 ${
        active
          ? "border-[#E4FF00] bg-[#E4FF00] text-black"
          : "border-[var(--line)] text-[var(--ink-soft)] hover:border-[var(--ink)] hover:text-[var(--ink)]"
      }`}
    >
      <span className="inline-flex items-center gap-1.5">
        {multi && active && <Check className="h-3.5 w-3.5" />}
        {children}
      </span>
    </button>
  );
}

function StepLabel({ n, label, hint }) {
  return (
    <div className="mb-4 flex items-center gap-3">
      <span className="ff-display flex h-6 w-6 items-center justify-center rounded-full bg-[#E4FF00] text-[12px] font-bold text-black">
        {n}
      </span>
      <span className="label text-[var(--ink-soft)]">{label}</span>
      {hint && <span className="text-[11px] tracking-wide text-[var(--ink-faint)]">({hint})</span>}
    </div>
  );
}

/* ─── Presentation: logos, distilled reasons, project usage ─── */
const TECH_LOGOS = {
  "React / Next.js": "nextdotjs",
  "React": "react",
  "React Native / PWA": "react",
  "FastAPI": "fastapi",
  "FastAPI + Python": "fastapi",
  "MongoDB": "mongodb",
  "MongoDB / PostgreSQL": "mongodb",
  "PostgreSQL / MongoDB": "postgresql",
  "WebSockets": "lucide:radio",
  "OpenAI + Python": "openai",
  "Stripe": "stripe",
  "Cloudflare": "cloudflare",
  "GSAP": "greensock",
  "Vercel": "vercel",
};

/* Short, punchy reasons per (project type + layer) for CORE; per slug for the rest. */
const REASONS_CORE = {
  "website-frontend": ["Server rendering for SEO", "Instant first paint", "Editable by your team"],
  "website-backend":  ["Forms and CMS endpoints", "Clean third-party hooks", "Easy to extend later"],
  "website-database": ["Flexible content models", "No painful migrations", "Iterate freely on schema"],

  "ecommerce-frontend": ["Fast, indexable product pages", "Custom checkout UX", "Tuned for conversion"],
  "ecommerce-backend":  ["Custom pricing and bundles", "Real stock and fulfilment logic", "Pluggable payments and shipping"],
  "ecommerce-database": ["Deeply nested catalogue data", "Variant-heavy products", "New attributes without migrations"],

  "saas-frontend": ["Component-driven dashboards", "Stateful, snappy UI", "Scales feature by feature"],
  "saas-backend":  ["Auth, roles, subscriptions", "Async, typed APIs", "Ready for mobile and integrations"],
  "saas-database": ["Flexible for product data", "Relational where money lives", "Iterate fast, stay correct"],

  "ai-frontend": ["Streaming chat interfaces", "Stateful conversations", "Easy to extend with tools"],
  "ai-backend":  ["Server-side prompt control", "Queues and cost guardrails", "Rich Python AI ecosystem"],
  "ai-database": ["Conversations at scale", "Knowledge base storage", "Vector-ready documents"],

  "internal-frontend": ["Dense, role-specific screens", "Workflow-driven layouts", "Built for your team's process"],
  "internal-backend":  ["Roles and permissions", "Automated workflows", "Audit-friendly endpoints"],
  "internal-database": ["Strict, relational records", "Audit trails included", "Reliable reporting"],

  "trading-frontend": ["Real-time charts and tables", "Smooth under live load", "Trader-grade density"],
  "trading-backend":  ["Async event processing", "Sub-second order handling", "Market-data fan-out"],
  "trading-database": ["High-write event capture", "Time-series friendly", "Strict where money matters"],

  "mobile-frontend": ["One codebase, two platforms", "Native feel on iOS and Android", "Or an installable PWA"],
  "mobile-backend":  ["Mobile-first API design", "Push notifications and sync", "Same backend for web later"],
  "mobile-database": ["Flexible across app versions", "Cloud sync built-in", "No painful migrations"],

  "other-frontend": ["Flexible component model", "Fits any custom product", "Maintainable as it grows"],
  "other-backend":  ["Typed, async APIs", "Clean integrations", "Ready for scale"],
  "other-database": ["Iterate without migrations", "Document-friendly model", "Pragmatic default"],
};

const REASONS_SLUG = {
  realtime:   ["Live prices and presence", "Persistent socket connection", "Low-latency, instant updates"],
  ai:         ["Real language understanding", "Tuneable for quality and cost", "Rich Python AI tooling"],
  payments:   ["PCI-compliant by default", "Cards, wallets, subscriptions", "Trusted global checkout"],
  edge:       ["Global CDN caching", "DDoS protection at the edge", "Fast everywhere, always on"],
  motion:     ["Choreographed scroll motion", "Cinematic transitions", "Premium first impression"],
  deployment: ["Global edge hosting", "Preview link per change", "Seconds to rollback"],
};

const PROJECTS_BY_SLUG = {
  frontend:   "Used across the marketing sites, dashboards and storefronts we build.",
  backend:    "Powers most of our API-driven products and internal systems.",
  database:   "Default for content-heavy and rapidly evolving products.",
  realtime:   "Used on trading dashboards, live ops tools and collaborative interfaces.",
  ai:         "Used in our assistant, automation and retrieval builds.",
  payments:   "Used wherever customers transact — SaaS, e-commerce and marketplaces.",
  edge:       "Used on high-traffic and globally distributed deployments.",
  motion:     "Used on premium marketing and brand sites where first impression matters.",
  deployment: "Default for nearly every production deployment at Parabolum.",
};

function getReasonsFor(type, c) {
  return REASONS_CORE[`${type}-${c.slug}`] || REASONS_SLUG[c.slug] || [];
}

function TechLogo({ tech }) {
  const slug = TECH_LOGOS[tech];
  const [err, setErr] = React.useState(false);
  if (!slug || slug === "lucide:radio" || err) {
    return <Radio className="h-6 w-6 text-white/85" strokeWidth={1.6} />;
  }
  return (
    <img
      src={`https://cdn.simpleicons.org/${slug}/ffffff`}
      alt={tech}
      width={28}
      height={28}
      loading="lazy"
      onError={() => setErr(true)}
      style={{ width: 28, height: 28, objectFit: "contain" }}
    />
  );
}

function RationaleRow({ label, accent, children }) {
  return (
    <div>
      <div className={`label text-[10px] ${accent ? "text-[#E4FF00]" : "text-white/35"}`}>{label}</div>
      <p className="mt-1.5 text-[13px] leading-relaxed text-white/65">{children}</p>
    </div>
  );
}

function TechCard({ c, type }) {
  const [open, setOpen] = useState(false);
  const reasons = getReasonsFor(type, c);
  const projects = PROJECTS_BY_SLUG[c.slug] || "Used across Parabolum projects of similar scale.";

  return (
    <div
      data-testid={`architect-card-${c.slug}`}
      className="group rounded-2xl border border-white/[0.08] bg-[#0a0a0a] p-5 transition-all duration-300 ease-out hover:-translate-y-[3px] hover:scale-[1.005] hover:border-white/[0.16] hover:bg-[#0d0d0d] md:p-6"
    >
      <div className="flex items-start gap-4">
        <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl border border-white/[0.06] bg-white/[0.03]">
          <TechLogo tech={c.tech} />
        </div>
        <div className="min-w-0 flex-1 pt-0.5">
          <div className="label text-[10px] text-white/35">{c.layer}</div>
          <div className="ff-display mt-1 truncate text-[17px] font-bold tracking-tight text-white">
            {c.tech}
          </div>
        </div>
      </div>

      <ul className="mt-5 space-y-2">
        {reasons.map((r, i) => (
          <li key={i} className="flex items-start gap-2.5 text-[13px] leading-relaxed text-white/70">
            <Check className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-[#E4FF00]" strokeWidth={2.5} />
            <span>{r}</span>
          </li>
        ))}
      </ul>

      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-cursor="big"
        data-testid={`architect-know-more-${c.slug}`}
        aria-expanded={open}
        className="mt-5 inline-flex items-center gap-1.5 text-[12px] font-medium tracking-tight text-white/55 transition-colors duration-200 hover:text-white"
      >
        <span>{open ? "Show less" : "Know more"}</span>
        <ChevronDown
          className={`h-3.5 w-3.5 transition-transform duration-300 ${open ? "rotate-180" : ""}`}
          strokeWidth={2}
        />
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="expand"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease }}
            style={{ overflow: "hidden" }}
          >
            <div
              className="mt-5 space-y-4 border-t border-white/[0.08] pt-5"
              data-testid={`architect-rationale-${c.slug}`}
            >
              <RationaleRow label="What it powers">{c.powers}</RationaleRow>
              <RationaleRow label="Why we use it" accent>{c.why}</RationaleRow>
              <RationaleRow label="Alternatives considered">
                <span className="font-medium text-white/85">Not {c.alt.name}</span>
                <span className="text-white/55"> — {c.alt.reason}</span>
              </RationaleRow>
              <RationaleRow label="Projects where we'd use it">{projects}</RationaleRow>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function ProjectArchitect({ onContact }) {
  const [type, setType] = useState(null);
  const [features, setFeatures] = useState([]);
  const [traffic, setTraffic] = useState(null);

  function toggleFeature(id) {
    setFeatures((f) => (f.includes(id) ? f.filter((x) => x !== id) : [...f, id]));
  }

  const ready = Boolean(type && traffic);

  const result = useMemo(() => {
    if (!ready) return null;
    const typeObj = PROJECT_TYPES.find((p) => p.id === type);
    const trafficObj = TRAFFIC.find((tr) => tr.id === traffic);
    const has = (id) => features.includes(id);
    const core = CORE[type] || CORE.other;

    const cards = [core.frontend, core.backend, core.database];

    if (type === "trading" || has("realtime") || has("chat")) cards.push(SPECIAL.realtime);
    if (type === "ai" || has("ai")) cards.push(SPECIAL.ai);
    if (type === "ecommerce" || has("payments")) cards.push(SPECIAL.payments);
    if (MOTION_TYPES.includes(type)) cards.push(motionCard(typeObj.label));
    if (trafficObj.weight >= 2) cards.push(edgeCard(trafficObj.label));
    cards.push(deploymentCard(typeObj.label));

    const score = typeObj.base + features.length * 0.45 + trafficObj.weight;
    let build;
    if (score <= 3) build = "2–4 weeks";
    else if (score <= 5.5) build = "4–6 weeks";
    else if (score <= 8.5) build = "6–10 weeks";
    else build = "12+ weeks";

    return { cards, build, typeName: typeObj.label };
  }, [ready, type, traffic, features]);

  return (
    <section
      id="architect"
      data-testid="architect-section"
      className="relative overflow-hidden bg-[var(--paper)] py-24 text-[var(--ink)] md:py-32"
    >
      <div className="relative mx-auto max-w-[1500px] px-5 md:px-10">
        <div className="max-w-3xl">
          <span className="label text-[var(--ink-soft)]">(05) — Project Architect</span>
          <h2 className="ff-display mt-6 text-5xl font-extrabold leading-[0.92] tracking-tight md:text-8xl">
            WE DON&apos;T BUILD
            <br />
            <span className="text-[#E4FF00] [-webkit-text-stroke:1px_rgba(10,10,10,0.18)]">
              RANDOM.
            </span>
          </h2>
          <p className="mt-6 max-w-2xl text-[15px] leading-relaxed text-[var(--ink-soft)]">
            Every project deserves an architecture tailored to its goals, scale and
            requirements. Describe your project and discover how we&apos;d engineer it —
            and why.
          </p>
        </div>

        <div className="mt-14 grid gap-8 lg:grid-cols-12 lg:gap-10">
          {/* ── Builder ── */}
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-24">
              <div className="rounded-[28px] border border-[var(--line)] bg-[var(--paper-2)] p-7 shadow-soft md:p-8">
                <div className="mb-8">
                  <StepLabel n={1} label="Project type" />
                  <div className="flex flex-wrap gap-2.5">
                    {PROJECT_TYPES.map((p) => (
                      <Chip key={p.id} active={type === p.id} onClick={() => setType(p.id)} testid={`architect-type-${p.id}`}>
                        {p.label}
                      </Chip>
                    ))}
                  </div>
                </div>

                <div className="mb-8">
                  <StepLabel n={2} label="Features" hint="optional" />
                  <div className="flex flex-wrap gap-2.5">
                    {FEATURES.map((f) => (
                      <Chip key={f.id} multi active={features.includes(f.id)} onClick={() => toggleFeature(f.id)} testid={`architect-feature-${f.id}`}>
                        {f.label}
                      </Chip>
                    ))}
                  </div>
                </div>

                <div>
                  <StepLabel n={3} label="Expected traffic" />
                  <div className="flex flex-wrap gap-2.5">
                    {TRAFFIC.map((tr) => (
                      <Chip key={tr.id} active={traffic === tr.id} onClick={() => setTraffic(tr.id)} testid={`architect-traffic-${tr.id}`}>
                        {tr.label}
                      </Chip>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ── Result ── */}
          <div className="lg:col-span-7">
            <AnimatePresence mode="wait">
              {!result ? (
                <motion.div
                  key="empty"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  data-testid="architect-result-empty"
                  className="flex min-h-[360px] flex-col items-start justify-center rounded-[28px] border border-dashed border-[var(--line)] bg-[var(--paper-2)] p-9"
                >
                  <div className="label mb-4 text-[var(--ink-faint)]">Recommended Architecture</div>
                  <p className="ff-serif max-w-md text-2xl leading-snug text-[var(--ink-soft)]">
                    Pick a project type and expected traffic — we&apos;ll engineer the
                    architecture and explain every decision.
                  </p>
                </motion.div>
              ) : (
                <motion.div
                  key={`${type}-${traffic}-${features.join(",")}`}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.5, ease }}
                  data-testid="architect-result"
                  className="relative overflow-hidden rounded-[28px] border border-white/10 bg-[#050505] text-white shadow-float"
                >
                  <div className="grid-faint pointer-events-none absolute inset-0 opacity-[0.05]" />

                  <div className="relative border-b border-white/[0.08] px-7 py-7">
                    <div className="ff-display text-2xl font-extrabold tracking-tight md:text-3xl">
                      For your {result.typeName}
                      <span className="ff-ui ml-3 text-[13px] font-medium text-white/35">
                        {result.cards.length} layers
                      </span>
                    </div>
                    <p className="ff-ui mt-3 max-w-xl text-[13px] leading-relaxed text-white/45">
                      Each layer is a deliberate choice. Tap <span className="text-white/70">Know more</span> on any card to see what it powers, why we use it, and what we considered instead.
                    </p>
                  </div>

                  <div className="relative grid gap-3 p-5 sm:grid-cols-2 md:gap-4 md:p-6">
                    {result.cards.map((c) => (
                      <TechCard key={c.slug} c={c} type={type} />
                    ))}
                  </div>

                  <div className="relative border-t border-white/[0.08] px-7 py-5">
                    <div className="label mb-1.5 text-white/35">Estimated build time</div>
                    <div className="ff-display text-3xl font-extrabold tracking-tight text-[#E4FF00]" data-testid="architect-buildtime">
                      {result.build}
                    </div>
                  </div>

                  <div className="relative flex flex-col gap-3 border-t border-white/[0.08] px-7 py-6 sm:flex-row">
                    <Magnetic strength={0.25}>
                      <button
                        type="button"
                        onClick={onContact}
                        data-cursor="big"
                        data-testid="architect-discuss"
                        className="btn-magnetic group flex w-full items-center justify-center gap-2.5 rounded-full bg-[#E4FF00] px-6 py-3.5 text-[14px] font-semibold text-black"
                      >
                        <span className="btn-label">Discuss this architecture</span>
                        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-black text-white transition-transform duration-500 group-hover:translate-x-1">
                          →
                        </span>
                      </button>
                    </Magnetic>
                    <button
                      type="button"
                      onClick={onContact}
                      data-cursor="big"
                      data-testid="architect-start"
                      className="inline-flex flex-1 items-center justify-center rounded-full border border-white/25 px-6 py-3.5 text-[14px] font-medium text-white/80 transition-colors hover:border-white hover:text-white"
                    >
                      Start a project
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}
